import currency from "currency.js";
import { GetMarkets200Response, SideResponse } from "../../codegen-api";
import { IOrderContextType } from "../../contexts/OrderContext";
import { InstrumentToTicker } from "../../hooks/wss/tickers/useTickersWSS";
import { IPositionMap } from "../../hooks/wss/usePositionsWSS.types";
import {
  IOptionVolume,
  ITableContentRowWithCallPut,
  ITickerWithInstrumentId,
} from "./OptionsTable";

// Default placeholder ticker value used while loading
const defaultTickerValue: ITickerWithInstrumentId = {
  ask: {
    price: "",
    volume: "",
    iv: "",
    orderVolume: 0,
  },
  bid: {
    price: "",
    volume: "",
    iv: "",
    orderVolume: 0,
  },
  instrumentId: 0,
  instrumentName: "",
  position: 0,
  side: SideResponse.Buy,
  delta: "",
  open_interest: "0",
  markPrice: "",
  iv: "",
};

const placeholderRow: ITableContentRowWithCallPut = {
  strike: "",
  call: defaultTickerValue,
  put: defaultTickerValue,
  expiry: 0,
};

interface IPutCalls {
  calls: GetMarkets200Response[];
  puts: GetMarkets200Response[];
}

// Calculate the total volume for calls and puts
export function computeTotalCallsPutsVolume(
  putCalls: IPutCalls,
  instrumentTicker: InstrumentToTicker
): IOptionVolume {
  const { puts, calls } = putCalls;
  const totalCallsVolume = calls.reduce(
    (prev, m) => {
      const ticker = instrumentTicker[m.instrument_name];
      return {
        bids: prev.bids + (Number(ticker?.bid?.amount) || 0),
        asks: prev.asks + (Number(ticker?.ask?.amount) || 0),
      };
    },
    {
      bids: 0,
      asks: 0,
    }
  );

  const totalPutsVolume = puts.reduce(
    (prev, m) => {
      const ticker = instrumentTicker[m.instrument_name];
      return {
        bids: prev.bids + (Number(ticker?.bid?.amount) || 0),
        asks: prev.asks + (Number(ticker?.ask?.amount) || 0),
      };
    },
    {
      bids: 0,
      asks: 0,
    }
  );
  return {
    calls: totalCallsVolume,
    puts: totalPutsVolume,
  };
}

// Returns rows, split into below strike price and above strike price
export function computeFilteredRows(
  currentIndexPrice: string | undefined,
  putCalls: IPutCalls,
  instrumentTicker: InstrumentToTicker,
  positionsMap: IPositionMap,
  orders: IOrderContextType["data"],
  expiry: number | undefined
): {
  lessThanCurrPriceRows: ITableContentRowWithCallPut[];
  moreThanCurrPriceRows: ITableContentRowWithCallPut[];
} {
  if (!currentIndexPrice) {
    return {
      lessThanCurrPriceRows: [
        placeholderRow,
        placeholderRow,
        placeholderRow,
        placeholderRow,
        placeholderRow,
      ],
      moreThanCurrPriceRows: [
        placeholderRow,
        placeholderRow,
        placeholderRow,
        placeholderRow,
        placeholderRow,
      ],
    };
  }

  const { puts, calls } = putCalls;
  const strikeToRow: Record<string, ITableContentRowWithCallPut> = {};

  calls.forEach((m) => {
    const ticker = instrumentTicker[m.instrument_name];
    if (m.strike && ticker) {
      const position = positionsMap[m.instrument_name];
      strikeToRow[m.strike] = {
        ...strikeToRow[m.strike],
        strike: m.strike,
        // Default value for put if doesnt already exist.
        put: strikeToRow[m.strike]?.put || defaultTickerValue,
        call: {
          delta: ticker.mark?.greeks?.delta || "0",
          open_interest: ticker.open_interest || "0",
          ask: {
            price: ticker.ask?.price || "0",
            volume: ticker.ask?.amount || "0",
            iv: ticker.ask?.greeks?.iv || "0",
            orderVolume: Number(
              orders?.find(
                (o) =>
                  o.instrument_id === ticker.instrument_id &&
                  o.side === SideResponse.Sell
              )?.amount || 0
            ),
          },
          bid: {
            price: ticker.bid?.price || "0",
            volume: ticker.bid?.amount || "0",
            iv: ticker.bid?.greeks?.iv || "0",
            orderVolume: Number(
              orders?.find(
                (o) =>
                  o.instrument_id === ticker.instrument_id &&
                  o.side === SideResponse.Buy
              )?.amount || 0
            ),
          },
          instrumentId: Number(m.instrument_id),
          instrumentName: m.instrument_name,
          position: Number(position?.amount || 0),
          side: position?.side || SideResponse.Buy,
          // Omitted mark stays empty (renders "-"), never a "0" quote.
          markPrice: ticker.mark?.price ?? "",
          iv: ticker.mark?.greeks?.iv || "0",
        },
        expiry: expiry!,
      };
    }
  });
  puts.forEach((m) => {
    const ticker = instrumentTicker[m.instrument_name];
    const position = positionsMap[m.instrument_name];
    if (m.strike && ticker) {
      strikeToRow[m.strike] = {
        ...strikeToRow[m.strike],
        strike: m.strike,
        // Default value for call if doesnt already exist.
        call: strikeToRow[m.strike]?.call || defaultTickerValue,
        put: {
          delta: ticker.mark?.greeks?.delta || "0",
          open_interest: ticker.open_interest || "0",
          ask: {
            price: ticker.ask?.price || "0",
            volume: ticker.ask?.amount || "0",
            iv: ticker.ask?.greeks?.iv || "0",
            orderVolume: Number(
              orders?.find(
                (o) =>
                  o.instrument_id === ticker.instrument_id &&
                  o.side === SideResponse.Sell
              )?.amount || 0
            ),
          },
          bid: {
            price: ticker.bid?.price || "0",
            volume: ticker.bid?.amount || "0",
            iv: ticker.bid?.greeks?.iv || "0",
            orderVolume: Number(
              orders?.find(
                (o) =>
                  o.instrument_id === ticker.instrument_id &&
                  o.side === SideResponse.Buy
              )?.amount || 0
            ),
          },
          instrumentId: Number(m.instrument_id),
          instrumentName: m.instrument_name,
          position: Number(position?.amount || 0),
          side: position?.side || SideResponse.Buy,
          // Omitted mark stays empty (renders "-"), never a "0" quote.
          markPrice: ticker.mark?.price ?? "",
          iv: ticker.mark?.greeks?.iv || "0",
        },
        expiry: expiry!,
      };
    }
  });

  const allRows = Object.values(strikeToRow);

  // Finally, split into 2 arrays, less than and more than current price
  let lessThanCurrPriceRows: ITableContentRowWithCallPut[] = [];
  let moreThanCurrPriceRows: ITableContentRowWithCallPut[] = [];

  if (allRows.length) {
    for (let i = 0; i < allRows.length; i += 1) {
      const row = allRows[i];
      if (currency(row.strike).value <= Number(currentIndexPrice)) {
        lessThanCurrPriceRows.push(row);
      } else {
        moreThanCurrPriceRows.push(row);
      }
    }
  } else {
    // Default values
    lessThanCurrPriceRows = [
      placeholderRow,
      placeholderRow,
      placeholderRow,
      placeholderRow,
      placeholderRow,
    ];
    moreThanCurrPriceRows = [
      placeholderRow,
      placeholderRow,
      placeholderRow,
      placeholderRow,
      placeholderRow,
    ];
  }

  return {
    lessThanCurrPriceRows,
    moreThanCurrPriceRows,
  };
}
